/**
 * Server-tool code-execution result block. Bordered card with stdout /
 * stderr panels and return code. Colored border reflects success vs.
 * failure.
 */

import { Code2 } from 'lucide-react';
import type { CodeExecutionToolResultBlock } from '@/lib/agent/types';
import { cn } from '@/lib/utils/cn';
import { normalizeTerminalOutput } from './helpers';

export interface CodeExecutionResultViewProps {
  block: CodeExecutionToolResultBlock;
  className?: string;
}

export function CodeExecutionResultView({ block, className }: CodeExecutionResultViewProps) {
  const content = block.content ?? {};
  const stdout = typeof content.stdout === 'string' ? normalizeTerminalOutput(content.stdout) : '';
  const stderr = typeof content.stderr === 'string' ? normalizeTerminalOutput(content.stderr) : '';
  const code = typeof content.return_code === 'number' ? content.return_code : null;
  const failed = code !== null && code !== 0;

  return (
    <div
      className={cn(
        'my-1.5 select-none rounded-md border',
        failed
          ? 'border-red-200/70 bg-red-50/40 dark:border-red-500/20 dark:bg-red-500/[0.06]'
          : 'border-emerald-200/70 bg-emerald-50/40 dark:border-emerald-500/20 dark:bg-emerald-500/[0.06]',
        className
      )}
    >
      <div
        className={cn(
          'flex select-none items-center gap-1.5 border-b px-3 py-1.5 font-medium text-[11px] uppercase tracking-wider',
          failed
            ? 'border-red-200/70 text-red-700 dark:border-red-500/20 dark:text-red-300'
            : 'border-emerald-200/70 text-emerald-700 dark:border-emerald-500/20 dark:text-emerald-300'
        )}
      >
        <Code2 className="size-3" />
        Code exec
        {code !== null && <span className="ml-auto">exit {code}</span>}
      </div>
      {stdout && (
        <pre className="agent-scroll max-h-64 select-text overflow-y-auto overflow-x-hidden whitespace-pre-wrap break-words px-3 py-2 font-mono text-[12.5px] text-gray-800 dark:text-gray-100">
          {stdout}
        </pre>
      )}
      {stderr && (
        <pre
          className={cn(
            'agent-scroll max-h-64 select-text overflow-y-auto overflow-x-hidden whitespace-pre-wrap break-words px-3 py-2 font-mono text-[12.5px] text-red-700 dark:text-red-300',
            stdout && 'border-red-200/50 border-t dark:border-red-500/20'
          )}
        >
          {stderr}
        </pre>
      )}
    </div>
  );
}
